import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

interface TooltipProps {
  /** Text shown in the tooltip bubble — also the trigger's accessible description. */
  label: React.ReactNode
  /** Single interactive element the tooltip describes. */
  children: React.ReactElement
  /** Preferred side of the trigger to render on; flips when there is no room. Default: top. */
  side?: 'top' | 'bottom'
  className?: string
}

/** Distance between trigger edge and bubble. */
const GAP = 8
/** Viewport breathing room kept when clamping — mirrors Select.tsx's margin. */
const MARGIN = 6
/**
 * Hover-intent delay. Sweeping the pointer across a toolbar of icon buttons
 * must not flash a bubble per button; only a deliberate rest opens one.
 * Keyboard focus bypasses this — a focused control is already a commitment.
 */
const HOVER_DELAY_MS = 150

type Side = 'top' | 'bottom'

interface Placement {
  /** The pinned anchor edge in viewport coords (bubble's bottom for `top` side). */
  top: number
  left: number
  /** Side actually used after flipping. */
  side: Side
  /** False once horizontal clamping kicked in, which drops -50% centering. */
  centered: boolean
}

const samePlacement = (a: Placement | null, b: Placement): boolean =>
  a !== null &&
  a.top === b.top &&
  a.left === b.left &&
  a.side === b.side &&
  a.centered === b.centered

/**
 * Lightweight, on-brand tooltip. Shows on hover (after a short intent delay),
 * keyboard focus and touch, is portal-rendered with fixed positioning (so it
 * escapes any `overflow-hidden` ancestor — table cells, cards, scroll
 * containers), flips/clamps itself into the viewport, and matches the Editorial
 * Dark surface tokens used by the custom Select / dropdown menus.
 *
 * Wrap a single interactive child:
 *   <Tooltip label="Reschedule"><button>…</button></Tooltip>
 *
 * Accessibility: the label also lives in a permanently mounted `sr-only` node
 * inside the wrapper, and the child's `aria-describedby` points at *that* node
 * rather than at the transient bubble (design-aesthetics §57). A bubble that
 * only exists on hover cannot describe anything at rest, and an icon-only or
 * `disabled` control — which can never be focused — would otherwise be nameless
 * to a screen reader. The visible bubble is `aria-hidden` so the label is
 * announced exactly once.
 *
 * Disabled children: browsers dispatch no mouse events on a `disabled` form
 * control and those events do not bubble to the wrapper, so the child gets
 * `pointer-events-none` and the wrapper becomes the hover target. The child's
 * `disabled` prop is left untouched — the control stays genuinely inert.
 */
export const Tooltip: React.FC<TooltipProps> = ({ label, children, side = 'top', className }) => {
  /** Trigger rect captured at open time; non-null means "bubble on screen". */
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const [placement, setPlacement] = useState<Placement | null>(null)
  const wrapRef = useRef<HTMLSpanElement | null>(null)
  const bubbleRef = useRef<HTMLSpanElement | null>(null)
  const showTimer = useRef<number | null>(null)
  const labelId = useId()
  const visible = anchor !== null

  const clearShowTimer = useCallback(() => {
    if (showTimer.current !== null) {
      window.clearTimeout(showTimer.current)
      showTimer.current = null
    }
  }, [])

  /**
   * Resolve the bubble position. Called twice per open: once with `bubble` null
   * (pre-measure, preferred side, centered) and once from a layout effect with
   * the real bubble box, which is what allows flipping and clamping. Both
   * passes run before paint, so nothing is ever seen in the wrong place.
   */
  const computePlacement = useCallback(
    (rect: DOMRect, bubble: { width: number; height: number } | null): Placement => {
      const vw = window.innerWidth
      const vh = window.innerHeight
      const bw = bubble?.width ?? 0
      const bh = bubble?.height ?? 0

      // Flip only when the preferred side cannot fit AND the other side is
      // roomier — a trigger in the top 30px of the window would otherwise
      // compute a negative top and render off-screen.
      let resolved: Side = side
      if (bh > 0) {
        const roomAbove = rect.top - GAP - MARGIN
        const roomBelow = vh - rect.bottom - GAP - MARGIN
        if (side === 'top' && roomAbove < bh && roomBelow > roomAbove) resolved = 'bottom'
        else if (side === 'bottom' && roomBelow < bh && roomAbove > roomBelow) resolved = 'top'
      }

      let top = resolved === 'top' ? rect.top - GAP : rect.bottom + GAP
      if (bh > 0) {
        // Neither side had room (a trigger taller than the viewport, a tiny
        // window): keep the bubble on screen rather than off the edge.
        top =
          resolved === 'top' ? Math.max(top, MARGIN + bh) : Math.min(top, vh - MARGIN - bh)
      }

      const centerX = rect.left + rect.width / 2
      if (bw > 0) {
        const idealLeft = centerX - bw / 2
        const clampedLeft = Math.max(MARGIN, Math.min(idealLeft, vw - bw - MARGIN))
        // Deviate from translate(-50%) centering only when clamping actually
        // moved the bubble, so nothing shifts for tooltips that already fit.
        if (Math.abs(clampedLeft - idealLeft) > 0.5) {
          return { top, left: clampedLeft, side: resolved, centered: false }
        }
      }
      return { top, left: centerX, side: resolved, centered: true }
    },
    [side],
  )

  /**
   * Measure the WRAPPER, not `firstElementChild`: after the `sr-only` label
   * node was added the wrapper has two element children, and a child rendering
   * a Fragment / `null` / bare text would make `firstElementChild` resolve to
   * the wrong box (or to the label node). The wrapper is `inline-flex` so it
   * tightly wraps the child, and the label node is `position: absolute` and
   * therefore contributes nothing to the wrapper's box. A collapsed wrapper
   * means the child rendered nothing — there is no anchor, so show nothing.
   */
  const openNow = useCallback(() => {
    clearShowTimer()
    const rect = wrapRef.current?.getBoundingClientRect()
    if (!rect || (rect.width === 0 && rect.height === 0)) return
    setAnchor(rect)
    setPlacement(computePlacement(rect, null))
  }, [clearShowTimer, computePlacement])

  const openAfterDelay = useCallback(() => {
    clearShowTimer()
    showTimer.current = window.setTimeout(() => {
      showTimer.current = null
      openNow()
    }, HOVER_DELAY_MS)
  }, [clearShowTimer, openNow])

  /** Hide is always immediate, and always kills a pending show. */
  const hide = useCallback(() => {
    clearShowTimer()
    setAnchor(null)
    setPlacement(null)
  }, [clearShowTimer])

  // Never leave a timer behind — 37 of these can be mounted at once.
  useEffect(() => clearShowTimer, [clearShowTimer])

  /** Second positioning pass, with the mounted bubble measured. */
  useLayoutEffect(() => {
    if (!anchor) return
    const bubble = bubbleRef.current?.getBoundingClientRect()
    if (!bubble) return
    const next = computePlacement(anchor, bubble)
    setPlacement((prev) => (samePlacement(prev, next) ? prev : next))
  }, [anchor, computePlacement])

  /**
   * Dismissal, registered only while a bubble is on screen — 37 mounted
   * Tooltips must not mean 37 idle listeners.
   *  - Escape closes (keyboard users can get the bubble out of the way).
   *  - Capture-phase `scroll` + `resize` close it: coords are captured once and
   *    the bubble is `position: fixed`, so any scroll strands it over unrelated
   *    content (design-aesthetics §54). Capture phase catches scrolls on inner
   *    containers, which do not bubble to `window`.
   *  - A touch/pointer press outside the wrapper closes the touch-opened
   *    bubble. The containment check also makes the opening tap safe: it can
   *    never be read as an outside press.
   */
  useEffect(() => {
    if (!visible) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide()
    }
    const onOutsidePress = (e: Event) => {
      const target = e.target as Node | null
      if (target && wrapRef.current?.contains(target)) return
      hide()
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    document.addEventListener('touchstart', onOutsidePress, true)
    document.addEventListener('pointerdown', onOutsidePress, true)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
      document.removeEventListener('touchstart', onOutsidePress, true)
      document.removeEventListener('pointerdown', onOutsidePress, true)
    }
  }, [visible, hide])

  // `children` is declared as a bare `React.ReactElement`, whose props generic
  // is `any`. Narrow it to the attributes we read and re-emit; nothing else
  // about the child is assumed.
  const child = children as React.ReactElement<{
    'aria-describedby'?: string
    className?: string
    disabled?: boolean
  }>
  const isDisabled = child.props.disabled === true
  // Merge, never clobber: the child may already point at help/error text. The
  // target is the permanent sr-only node, so the id is never dangling.
  const describedBy = [child.props['aria-describedby'], labelId].filter(Boolean).join(' ')
  const clonedProps: { 'aria-describedby': string; className?: string } = {
    'aria-describedby': describedBy,
  }
  if (isDisabled) clonedProps.className = cn(child.props.className, 'pointer-events-none')

  return (
    <span
      ref={wrapRef}
      onMouseEnter={openAfterDelay}
      onMouseLeave={hide}
      onFocus={openNow}
      onBlur={hide}
      // Activating the trigger drops the bubble so it does not linger over the
      // thing just clicked. For touch input `pointerdown` precedes `touchstart`
      // (Pointer Events L2), so the tap still ends up showing the tooltip.
      onPointerDown={hide}
      onTouchStart={openNow}
      className="inline-flex"
    >
      {React.cloneElement(child, clonedProps)}
      {/*
        Kept last and out of flow so wrapper measurement stays deterministic.
        This — not the bubble — is what `aria-describedby` resolves to.
      */}
      <span id={labelId} className="sr-only select-none">
        {label}
      </span>
      {placement &&
        createPortal(
          <span
            ref={bubbleRef}
            role="tooltip"
            // Purely visual: the sr-only node above already carries the label,
            // and announcing both would double it up.
            aria-hidden="true"
            style={{
              position: 'fixed',
              top: placement.top,
              left: placement.left,
              transform: placement.centered
                ? placement.side === 'top'
                  ? 'translate(-50%, -100%)'
                  : 'translate(-50%, 0)'
                : placement.side === 'top'
                  ? 'translateY(-100%)'
                  : undefined,
              zIndex: 1100,
            }}
            className={cn(
              'pointer-events-none select-none whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-medium leading-none shadow-lg',
              'bg-[#0f1011] text-[#f7f8f8]',
              'dark:bg-[#191a1b] dark:border dark:border-white/[0.08] dark:shadow-[0_8px_24px_rgba(0,0,0,0.45)]',
              className,
            )}
          >
            {label}
          </span>,
          document.body,
        )}
    </span>
  )
}
